import React, { useState, useEffect } from 'react';
import GoogleSignInButton from '../components/common/GoogleSignInButton';
import FormInput from '../components/common/FormInput';
import PasswordInput from '../components/common/PasswordInput';
import WelcomeBack from '../components/illustrations/WelcomeBack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import toast from 'react-hot-toast';

const SignIn = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loginWithGoogle } = useAuth();

  // Check for session expired message
  useEffect(() => {
    if (searchParams.get('session_expired') === 'true') {
      toast.error('Your session has expired. Please sign in again.', {
        duration: 5000,
        id: 'session-expired' // Prevent duplicate toasts
      });
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      return;
    }

    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        // Check if email is verified
        if (!result.user?.email_confirmed) {
          navigate('/verify-email');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Sign in error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      await loginWithGoogle();
      // Google OAuth will redirect, so no need to navigate here
    } catch (error) {
      console.error('Google sign in error:', error);
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <div className="flex min-h-screen font-sans">
      {/* Left side - Form */}
      <div className="w-full md:w-3/5 lg:w-2/3 flex items-center justify-center px-4 py-8 sm:px-6 sm:py-10 md:px-8 md:py-12 bg-white relative">
        <div className="w-full max-w-sm sm:max-w-md">
          {/* Welcome text */}
          <h2 className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
            Welcome Back!
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
            Let's make your data collection fun again.
          </p>

          {/* Google Sign In */}
          <GoogleSignInButton 
            text="Sign in with Google"
            onClick={handleGoogleSignIn}
            disabled={loading}
          />

          {/* Divider */}
          <div className="flex items-center my-5 sm:my-6 gap-4">
            <div className="flex-1 h-px bg-gray-300" />
            <span className="text-xs sm:text-sm text-gray-500">
              Or sign in with email
            </span>
            <div className="flex-1 h-px bg-gray-300" />
          </div>

          {/* Sign In Form */}
          <form onSubmit={handleSubmit}>
            <FormInput
              label="Email"
              type="email"
              name="email"
              placeholder="Your email..."
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading}
            />

            <PasswordInput
              label="Password"
              name="password"
              placeholder="Your Password..."
              value={formData.password}
              onChange={handleChange}
              required
              disabled={loading}
              showForgot={true}
              onForgotClick={handleForgotPassword}
            />

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full px-4 py-3 sm:py-4 rounded-xl text-sm sm:text-base font-semibold mt-2 transition-all duration-300
                ${loading
                  ? 'bg-gray-400 cursor-not-allowed opacity-60'
                  : 'bg-blue-600 hover:bg-blue-700 hover:-translate-y-0.5 active:translate-y-0'
                } text-white`}
            >
              {loading ? 'SIGNING IN...' : 'SIGN IN'}
            </button>
          </form>

          {/* Sign Up Link */}
          <p className="text-center mt-5 sm:mt-6 text-sm sm:text-base text-gray-700">
            Don't have an account?{' '}
            <a
              onClick={() => !loading && navigate('/signup')}
              className={`text-blue-600 font-semibold no-underline ${loading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:text-blue-700'}`}
            >
              Sign Up
            </a>
          </p>
        </div>
      </div>

      {/* Right side - Illustration */}
      <div className="hidden md:flex md:w-2/5 lg:w-1/3 relative">
        <WelcomeBack />
      </div>
    </div>
  );
};

export default SignIn;